import React from 'react';
import { Constants } from '../../../Models/Constants';
import { IRangeValidation } from '../../../Models/IRangeValidation';
import { getAppInsights } from '../../../Services/Global';
import { setErrorMessage, validateInputFields } from './ProductDesignFormUtils';
import { isOutOfDisplayRange, isDisplayValueDecimal } from '../../../Utils/UnitsHelper';
import { ProductDesignFormState, ProductDesignFormAction } from './ProductDesignFormTypes';

/**
 * Handles form input changes for the ProductDesignForm component
 * Manages validation, error handling, and state updates for both single and multi-section configurations
 */
export function handleFormChange(
  e: React.FormEvent<HTMLInputElement> | any,
  state: ProductDesignFormState,
  dispatch: React.Dispatch<ProductDesignFormAction>
): void {
  const appInsights = getAppInsights();

  // Hide copy button if an input changes to make it clear that current form values aren't taken into account.
  dispatch({ type: 'SET_DESIGN_RECEIVED', payload: false });
  dispatch({ type: 'SET_SHOW_XML_MESSAGE', payload: false });

  if (state.firstInteraction === undefined) {
    dispatch({ type: 'SET_FIRST_INTERACTION', payload: new Date() });
    appInsights?.trackEvent({ name: 'useraction:FirstInteraction', properties: { 'timestamp': new Date().toISOString() } });
    appInsights?.flush();
  }

  dispatch({ type: 'SET_DISABLE_SUBMIT', payload: !document.forms[0].checkValidity() });

  let val = e.currentTarget ? e.currentTarget.value : e.target.value;
  let inputName = e.currentTarget ? e.currentTarget.name : e.target.name;
  let range: IRangeValidation;
  let inputError: boolean = false;

  dispatch({ type: 'SET_INPUT_VALUES', payload: { ...state.inputValues, [inputName]: val } });

  // If the unit configuration is not multi section we can handle the inputs differently than if it is multi section  
  if (!state.unitConfiguration?.isMultiSection) {

    const firstCharLowercaseInputName = inputName.charAt(0).toLowerCase() + inputName.slice(1) as keyof typeof state;

    const nextState = {
      ...state,
      [firstCharLowercaseInputName]: val,
      [`${firstCharLowercaseInputName}Error`]: ''
    };

    switch (inputName) {
      case Constants.ColdMediaTempIn:
        let error = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaTempIn) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, 'temperature')) {
            inputError = true;
            dispatch({ type: 'SET_COLD_MEDIA_TEMP_IN_ERROR', payload: Constants.VALUE_SHOULD_NOT_BE_DECIMAL });
          }

          // Validate in the displayed unit at displayed precision (see isOutOfDisplayRange)
          if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, 'temperature')) {
            inputError = true;
            error = setErrorMessage(range?.lessThan, range?.greaterThan, 'temperature');
          }

          dispatch({ type: 'SET_COLD_MEDIA_TEMP_IN_ERROR', payload: error });
        }

        dispatch({ type: 'SET_COLD_MEDIA_TEMP_IN', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedState = { ...state, coldMediaTempInError: error };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedState, false) });
        break;
      case Constants.ColdMediaTempOut:
        let errorOut = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaTempOut) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, 'temperature')) {
            inputError = true;
            errorOut = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_COLD_MEDIA_TEMP_OUT_ERROR', payload: errorOut });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, 'temperature')) {
            inputError = true;
            errorOut = setErrorMessage(range?.lessThan, range?.greaterThan, 'temperature');
            dispatch({ type: 'SET_COLD_MEDIA_TEMP_OUT_ERROR', payload: errorOut });
          } else {
            errorOut = '';
            dispatch({ type: 'SET_COLD_MEDIA_TEMP_OUT_ERROR', payload: errorOut });
          }
        }
        dispatch({ type: 'SET_COLD_MEDIA_TEMP_OUT', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStateOut = { ...state, coldMediaTempOutError: errorOut };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateOut, false) });
        break;
      case Constants.ColdMediaFlowrate:
        let errorFlowrate = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaFlowrate) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow')) {
            inputError = true;
            errorFlowrate = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_COLD_MEDIA_FLOWRATE_ERROR', payload: errorFlowrate });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow')) {
            inputError = true;
            errorFlowrate = setErrorMessage(range?.lessThan, range?.greaterThan, state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow');
            dispatch({ type: 'SET_COLD_MEDIA_FLOWRATE_ERROR', payload: errorFlowrate });
          } else {
            errorFlowrate = '';
            dispatch({ type: 'SET_COLD_MEDIA_FLOWRATE_ERROR', payload: errorFlowrate });
          }
        }
        dispatch({ type: 'SET_COLD_MEDIA_FLOWRATE', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStateFlowrate = { ...state, coldMediaFlowrateError: errorFlowrate };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateFlowrate, false) });
        break;
      case Constants.ColdMediaPressureDrop:
        let errorPressureDrop = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.ColdMediaPressureDrop) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, 'pressure')) {
            inputError = true;
            errorPressureDrop = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_COLD_MEDIA_PRESSURE_DROP_ERROR', payload: errorPressureDrop });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, 'pressure')) {
            inputError = true;
            errorPressureDrop = setErrorMessage(range?.lessThan, range?.greaterThan, 'pressure');
            dispatch({ type: 'SET_COLD_MEDIA_PRESSURE_DROP_ERROR', payload: errorPressureDrop });
          } else {
            errorPressureDrop = '';
            dispatch({ type: 'SET_COLD_MEDIA_PRESSURE_DROP_ERROR', payload: errorPressureDrop });
          }
        }
        dispatch({ type: 'SET_COLD_MEDIA_PRESSURE_DROP', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStatePressureDrop = { ...state, coldMediaPressureDropError: errorPressureDrop };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStatePressureDrop, false) });
        break;

      case Constants.HotMediaTempIn:
        let errorHotTempIn = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaTempIn) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, 'temperature')) {
            inputError = true;
            errorHotTempIn = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_HOT_MEDIA_TEMP_IN_ERROR', payload: errorHotTempIn });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, 'temperature')) {
            inputError = true;
            errorHotTempIn = setErrorMessage(range?.lessThan, range?.greaterThan, 'temperature');
            dispatch({ type: 'SET_HOT_MEDIA_TEMP_IN_ERROR', payload: errorHotTempIn });
          } else {
            errorHotTempIn = '';
            dispatch({ type: 'SET_HOT_MEDIA_TEMP_IN_ERROR', payload: errorHotTempIn });
          }
        }

        dispatch({ type: 'SET_HOT_MEDIA_TEMP_IN', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStateHotTempIn = { ...state, hotMediaTempInError: errorHotTempIn };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateHotTempIn, false) });
        break;
      case Constants.HotMediaTempOut:
        let errorHotTempOut = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaTempOut) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, 'temperature')) {
            inputError = true;
            errorHotTempOut = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_HOT_MEDIA_TEMP_OUT_ERROR', payload: errorHotTempOut });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, 'temperature')) {
            inputError = true;
            errorHotTempOut = setErrorMessage(range?.lessThan, range?.greaterThan, 'temperature');
            dispatch({ type: 'SET_HOT_MEDIA_TEMP_OUT_ERROR', payload: errorHotTempOut });
          } else {
            errorHotTempOut = '';
            dispatch({ type: 'SET_HOT_MEDIA_TEMP_OUT_ERROR', payload: errorHotTempOut });
          }
        }

        dispatch({ type: 'SET_HOT_MEDIA_TEMP_OUT', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStateHotTempOut = { ...state, hotMediaTempOutError: errorHotTempOut };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateHotTempOut, false) });
        break;
      case Constants.HotMediaFlowrate:
        let errorHotFlowrate = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaFlowrate) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow')) {
            inputError = true;
            errorHotFlowrate = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_HOT_MEDIA_FLOWRATE_ERROR', payload: errorHotFlowrate });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow')) {
            inputError = true;
            errorHotFlowrate = setErrorMessage(range?.lessThan, range?.greaterThan, state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow');
            dispatch({ type: 'SET_HOT_MEDIA_FLOWRATE_ERROR', payload: errorHotFlowrate });
          }
          else {
            errorHotFlowrate = '';
            dispatch({ type: 'SET_HOT_MEDIA_FLOWRATE_ERROR', payload: errorHotFlowrate });
          }
        }

        dispatch({ type: 'SET_HOT_MEDIA_FLOWRATE', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStateHotFlowrate = { ...state, hotMediaFlowrateError: errorHotFlowrate };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateHotFlowrate, false) });
        break;
      case Constants.HotMediaPressureDrop:
        let errorHotPressureDrop = '';
        if (state.rangeValidations?.length !== 0) {
          range = state.rangeValidations?.find(v => v.propertyName === Constants.HotMediaPressureDrop) as IRangeValidation;
          
          // Convert val to number for consistent comparison
          const numericVal = parseFloat(val);
          
          if (!range.isDecimal && isDisplayValueDecimal(numericVal, 'pressure')) {
            inputError = true;
            errorHotPressureDrop = Constants.VALUE_SHOULD_NOT_BE_DECIMAL;
            dispatch({ type: 'SET_HOT_MEDIA_PRESSURE_DROP_ERROR', payload: errorHotPressureDrop });
          }
          else if (isOutOfDisplayRange(numericVal, range?.lessThan, range?.greaterThan, 'pressure')) {
            inputError = true;
            errorHotPressureDrop = setErrorMessage(range?.lessThan, range?.greaterThan, 'pressure');
            dispatch({ type: 'SET_HOT_MEDIA_PRESSURE_DROP_ERROR', payload: errorHotPressureDrop });
          } else {
            errorHotPressureDrop = '';
            dispatch({ type: 'SET_HOT_MEDIA_PRESSURE_DROP_ERROR', payload: errorHotPressureDrop });
          }
        }

        dispatch({ type: 'SET_HOT_MEDIA_PRESSURE_DROP', payload: val });
        // Always validate form errors - don't override with inputError
        const updatedStateHotPressureDrop = { ...state, hotMediaPressureDropError: errorHotPressureDrop };
        dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateHotPressureDrop, false) });
        break;
      default:
        const prop = state.userInputs?.find(x => x.name === inputName);
        if (prop) {
          if (prop.lessThan !== undefined && prop.greaterThan !== undefined) {
            dispatch({ type: 'SET_USER_INPUTS', payload: state.userInputs!.map((input) =>
              input.name === inputName
                ? { ...input, errorMessage: "" }
                : input
            ) });
            
            // Convert val to number for consistent comparison
            const numericVal = parseFloat(val);
            
            if (val !== '' && isOutOfDisplayRange(numericVal, prop.lessThan, prop.greaterThan, prop.unitType)) {
              dispatch({ type: 'SET_USER_INPUTS', payload: state.userInputs!.map((input) =>
                input.name === inputName
                  ? { ...input, errorMessage: setErrorMessage(prop.lessThan!, prop.greaterThan!, prop.unitType) }
                  : input
              ) });
            }
          }
        }
        break;
    }
  }
  else if (state.unitConfiguration?.isMultiSection) {
    const prop = state.userInputs?.find(x => x.name === inputName);
    if (prop) {
      // Create updated inputs array with new value - calculate everything before dispatching
      let newUserInputs = state.userInputs!.map((input) =>
        input.name === inputName
          ? { ...input, errorMessage: '', value: val }
          : input
      );
      
      if (!prop.isDecimal && isDisplayValueDecimal(parseFloat(val), prop.unitType)) {
        newUserInputs = newUserInputs.map((input) =>
          input.name === inputName
            ? { ...input, errorMessage: Constants.VALUE_SHOULD_NOT_BE_DECIMAL }
            : input
        );
      }
      else if (prop.lessThan !== undefined && prop.greaterThan !== undefined) {
        // Convert val to number for consistent comparison
        const numericVal = parseFloat(val);
        
        // Check if value is out of range
        // Note: lessThan represents the lower bound (min) and greaterThan represents the upper bound (max)
        // So we check if value < min OR value > max
        if (val !== '' && isOutOfDisplayRange(numericVal, prop.lessThan, prop.greaterThan, prop.name.includes('Pressure') ? 'pressure' : prop.name.includes('Temp') ? 'temperature' : state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow')) {
          newUserInputs = newUserInputs.map((input) =>
            input.name === inputName
              ? { ...input, errorMessage: setErrorMessage(prop.lessThan!, prop.greaterThan!, prop.name.includes('Pressure') ? 'pressure' : prop.name.includes('Temp') ? 'temperature' : state.unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow') }
              : input
          );
        }
      }
      
      // Single dispatch to update state with final values
      dispatch({ type: 'SET_USER_INPUTS', payload: newUserInputs });
      
      // Validate form using updated inputs
      // We need to construct a temporary state object with the new inputs to validate correctly
      // because 'state' in this scope is stale (from closure)
      const updatedStateForValidation = { ...state, userInputs: newUserInputs };
      dispatch({ type: 'SET_FORM_HAS_ERRORS', payload: validateInputFields(updatedStateForValidation) });
    }
  }
} 